// Shared with discovery-react, which also reads and writes this record. Do not rename the key or change
// the `{ sessionId, firstSeenAt, hidden }` shape without updating it there.

export const DISCOVERY_CODE_RECORD_KEY = 'fs.discovery.discoveryCode'
export const DISCOVERY_CODE_WINDOW_MS = 2 * 60 * 60 * 1000

const isWellFormed = (value) =>
  !!value && typeof value.sessionId === 'string' && value.sessionId.length > 0 && Number.isFinite(value.firstSeenAt)

/**
 * Reads the stored Discovery Code record.
 * @returns {{sessionId: string, firstSeenAt: number, hidden: boolean} | null} the record, or null when
 * absent, unparseable, or malformed. A missing `hidden` is coerced to `false`.
 */
export function readDiscoveryCodeRecord() {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(DISCOVERY_CODE_RECORD_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!isWellFormed(parsed)) return null
    return { sessionId: parsed.sessionId, firstSeenAt: parsed.firstSeenAt, hidden: !!parsed.hidden }
  } catch {
    return null
  }
}

export function writeDiscoveryCodeRecord(record) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(DISCOVERY_CODE_RECORD_KEY, JSON.stringify(record))
  } catch {
    // ignore
  }
}

export function hideDiscoveryCodeRecord() {
  const record = readDiscoveryCodeRecord()
  if (!record) return
  writeDiscoveryCodeRecord({ ...record, hidden: true })
}

/**
 * Whether the two-hour display window has lapsed for `record`. A null or malformed record counts as expired.
 */
export function isDiscoveryCodeWindowExpired(record, now = Date.now()) {
  if (!isWellFormed(record)) return true
  return now - record.firstSeenAt >= DISCOVERY_CODE_WINDOW_MS
}

// Local to discovery-kids. discovery-react resets `hidden` to false on rescan, so the shared record alone
// cannot tell a rescan-restore apart from a first reveal; this remembers which sessionId this device hid.
export const DISCOVERY_CODE_HIDDEN_ONCE_KEY = 'fs.discovery.discoveryCodeHiddenOnce'

export function markDiscoveryCodeHiddenOnce(sessionId) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(DISCOVERY_CODE_HIDDEN_ONCE_KEY, sessionId)
  } catch {
    // ignore
  }
}

/**
 * Whether this device hid `sessionId`. Clears the breadcrumb when it matches.
 * @returns {boolean}
 */
export function consumeDiscoveryCodeHiddenOnce(sessionId) {
  if (typeof window === 'undefined' || !sessionId) return false
  try {
    const stored = window.localStorage.getItem(DISCOVERY_CODE_HIDDEN_ONCE_KEY)
    if (stored !== sessionId) return false
    window.localStorage.removeItem(DISCOVERY_CODE_HIDDEN_ONCE_KEY)
    return true
  } catch {
    return false
  }
}

export const DISCOVERY_CODE_NATURAL_EXPIRY_FIRED_KEY = 'fs.discovery.discoveryCodeNaturalExpiryFiredFor'

export function hasFiredDiscoveryCodeNaturalExpiry(sessionId) {
  if (typeof window === 'undefined' || !sessionId) return false
  try {
    return window.localStorage.getItem(DISCOVERY_CODE_NATURAL_EXPIRY_FIRED_KEY) === sessionId
  } catch {
    return false
  }
}

export function markDiscoveryCodeNaturalExpiryFired(sessionId) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(DISCOVERY_CODE_NATURAL_EXPIRY_FIRED_KEY, sessionId)
  } catch {
    // ignore
  }
}
